/**
 * Piezas compartidas del portal público: la tarjeta de partido, la tirita de
 * parciales, los chips de estado y los formatos de fecha.
 *
 * Están juntas acá porque las tres pantallas (portada, resultados, ficha de
 * competencia) muestran EL MISMO partido con distinto marco: si cada una
 * dibujara su propia tarjeta, la de la portada y la del fixture terminarían
 * mostrando cosas distintas del mismo encuentro, que es exactamente lo que un
 * dirigente nota en la demo.
 */
import type { ReactNode } from 'react';
import { getLocale, LOCALE_TAGS } from '../../i18n/index';
import { useT } from '../../i18n/use-t';
import type { PublicFixtureEntry, PublicSetLine } from '../../lib/public-client';
import { publicHref, publicMatchPath } from '../../lib/public-router';

// ---------------------------------------------------------------------------
// Formatos
// ---------------------------------------------------------------------------

/**
 * Hora local del que mira. El API manda ISO con zona y el browser lo pasa a la
 * del espectador — que es la única forma correcta: el server no sabe si el que
 * abre el portal está en Salta, en Madrid o en San Pablo.
 */
export function formatTime(iso: string | null): string {
  if (iso === null) return '';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  return at.toLocaleTimeString(LOCALE_TAGS[getLocale()], { hour: '2-digit', minute: '2-digit' });
}

export function formatDate(iso: string | null): string {
  if (iso === null) return '';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  return at.toLocaleDateString(LOCALE_TAGS[getLocale()], {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

/** "hace 4 s" / "hace 2 min": la frescura del dato, dicha sin reloj absoluto. */
export function useAgo(): (since: number | null) => string {
  const t = useT();
  return (since) => {
    if (since === null) return '';
    const seconds = Math.max(0, Math.round((Date.now() - since) / 1000));
    if (seconds < 60) return t('publicPortal.agoSeconds', { seconds });
    return t('publicPortal.agoMinutes', { minutes: Math.round(seconds / 60) });
  };
}

// ---------------------------------------------------------------------------
// Navegación
// ---------------------------------------------------------------------------

/**
 * `onClick` de un link interno que NO rompe el browser.
 *
 * Los links del portal público son `<a href>` de verdad, no `<a href="#">`, y
 * este handler deja pasar el ctrl/cmd/shift-click y el botón del medio.
 * Interceptar todo es lo que rompe "abrir en pestaña nueva" y "copiar
 * dirección" en un portal cuya razón de ser es que la gente comparta el link.
 */
export function interceptable(
  href: string,
  onNavigate: (href: string) => void,
): (event: React.MouseEvent<HTMLAnchorElement>) => void {
  return (event) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (event.button !== 0) return;
    event.preventDefault();
    onNavigate(href);
  };
}

// ---------------------------------------------------------------------------
// Chips
// ---------------------------------------------------------------------------

export function StatusChip({ entry }: { entry: PublicFixtureEntry }): JSX.Element | null {
  const t = useT();
  switch (entry.status) {
    case 'live':
      return <span className="pub-chip live">{t('publicPortal.statusLive')}</span>;
    case 'walkover':
      return <span className="pub-chip">{t('publicPortal.statusWalkover')}</span>;
    case 'postponed':
      return <span className="pub-chip">{t('publicPortal.statusPostponed')}</span>;
    case 'cancelled':
      return <span className="pub-chip">{t('publicPortal.statusCancelled')}</span>;
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Tarjeta de partido
// ---------------------------------------------------------------------------

/**
 * Un partido, en la forma en que se lee de un vistazo: arriba el contexto
 * (competencia, fecha, hora, sede), abajo los dos equipos con sus sets y los
 * parciales.
 *
 * Es un `<a>` de verdad cuando el partido tiene ficha, y un `<div>` cuando no
 * (todavía no se sincronizó): un link que lleva a un 404 en la cara pública de
 * la federación es peor que no tener link.
 */
export function MatchCard({
  slug,
  base,
  entry,
  onNavigate,
}: {
  slug: string;
  base: string;
  entry: PublicFixtureEntry;
  onNavigate: (href: string) => void;
}): JSX.Element {
  const t = useT();
  const home = entry.home?.name ?? t('publicPortal.tbd');
  const away = entry.away?.name ?? t('publicPortal.tbd');
  const result = entry.result;
  const homeWon = result !== null && result.setsHome > result.setsAway;
  const awayWon = result !== null && result.setsAway > result.setsHome;

  const inner = (
    <>
      <div className="pub-match-top">
        <span>{entry.competitionName}</span>
        {entry.groupName !== null ? (
          <>
            <span className="sep">·</span>
            <span>{entry.groupName}</span>
          </>
        ) : null}
        <span className="sep">·</span>
        <span>{t('publicPortal.matchday', { matchday: entry.matchday })}</span>
        {entry.scheduledAt !== null ? (
          <>
            <span className="sep">·</span>
            <span>
              {formatDate(entry.scheduledAt)} {formatTime(entry.scheduledAt)}
            </span>
          </>
        ) : null}
        <StatusChip entry={entry} />
      </div>

      <TeamRow
        name={home}
        sets={result === null ? null : result.setsHome}
        partials={result?.sets.map((s) => s.home) ?? []}
        lost={awayWon}
      />
      <TeamRow
        name={away}
        sets={result === null ? null : result.setsAway}
        partials={result?.sets.map((s) => s.away) ?? []}
        lost={homeWon}
      />

      {entry.venue !== null ? <p className="pub-note">{entry.venue}</p> : null}
    </>
  );

  const className = `pub-match${entry.status === 'live' ? ' is-live' : ''}`;

  if (entry.matchId === null) {
    return <div className={className}>{inner}</div>;
  }

  const href = publicHref(publicMatchPath(slug, entry.matchId), base);
  return (
    <a className={className} href={href} onClick={interceptable(href, onNavigate)}>
      {inner}
    </a>
  );
}

function TeamRow({
  name,
  sets,
  partials,
  lost,
}: {
  name: string;
  sets: number | null;
  partials: readonly number[];
  lost: boolean;
}): JSX.Element {
  return (
    <div className={`pub-team${lost ? ' lost' : ''}`}>
      <span className="pub-team-name">{name}</span>
      <span className="pub-team-partials">
        {partials.map((value, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <b key={index}>{value}</b>
        ))}
      </span>
      <span className="pub-team-sets">{sets ?? '–'}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bloques de página
// ---------------------------------------------------------------------------

export function Section({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  return (
    <section className="pub-section">
      <h2>
        {title}
        {action}
      </h2>
      {children}
    </section>
  );
}

export function EmptyBlock({ children }: { children: ReactNode }): JSX.Element {
  return <p className="pub-empty">{children}</p>;
}

/** Tirita de parciales de la ficha del partido (marca el set en juego). */
export function SetPills({ sets }: { sets: readonly PublicSetLine[] }): JSX.Element | null {
  if (sets.length === 0) return null;
  return (
    <span className="pub-team-partials">
      {sets.map((set) => (
        <b key={set.set} className={set.live ? 'live' : undefined}>
          {set.home}-{set.away}
        </b>
      ))}
    </span>
  );
}
